import chunk from 'lodash/chunk'
import React, { useMemo } from 'react'
import type { PlaylistStats } from 'types/playlist'
import styles from './index.module.css'

type Props = {
  stats: PlaylistStats
}

/**
 * PlaylistStatsSummary component for displaying playlist statistics
 *
 * Displays key playlist metrics including page views, playlist downloads,
 * track downloads, and streams in a grid layout. Data is organized into
 * rows of two items each.
 *
 * @param props - Component props
 * @param props.stats - Playlist statistics data containing access and engagement metrics
 * @returns Memoized JSX element displaying the stats summary grid
 */
const PlaylistStatsSummary = ({ stats }: Props) => {
  const data = useMemo(
    () => [
      {
        caption: 'Page views',
        value: stats.playlist_accesses,
      },
      {
        caption: 'Playlist downloads',
        value: stats.playlist_downloads,
      },
      {
        caption: 'Track downloads',
        value: stats.track_downloads,
      },
      {
        caption: 'Streams',
        value: stats.track_streams,
      },
    ],
    [stats],
  )
  return (
    <div className={styles.playlistStatsSummary}>
      {chunk(data, 2).map((row, index) => (
        <div className={styles.playlistStatsSummaryRow} key={index}>
          {row.map((item, index) => (
            <div className={styles.playlistStatsSummaryItem} key={index}>
              <div className={styles.playlistStatsSummaryItemValue}>
                {item.value}
              </div>
              <div className={styles.playlistStatsSummaryItemCaption}>
                {item.caption}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export default React.memo<Props>(PlaylistStatsSummary)
